import {
    AnimationBaseController,
    Environment,
    EnvironmentTypeEnum,
    IAnimationControllerConfiguration,
    Inject,
    ModelEvents,
    ResourceItem,
    ResourceItemType,
    SoundType,
} from '@denishussar/html5-game-engine';
import { AnimationContents } from '../../../AnimationContents';
import {
    GameCollectableTypeEnum,
    GameModel,
    GameMoveableObstacleTypeEnum,
    GameObstacleTypeEnum,
    RoadObjectType,
} from '../../Models/GameModel';
import { GameLogic } from '../../Logic/GameLogic';
import { Container, Graphics, Point, projection, Sprite, Texture } from 'pixi.js';

import 'pixi-projection';
import {
    BACKGROUND_LINE_IMG,
    BACKGROUND_ROAD_CENTER_IMG,
    IHighway,
    Level1,
    LVL2_BACKGROUND_LINE_IMG,
    LVL2_BACKGROUND_ROAD_CENTER_IMG,
    SNOW_ROAD,
    VFX_ACCELERATION,
    VFX_GLOW,
    VFX_RAY,
} from '../../Constants/ImageConstants';
import { GamePlayerController } from './GamePlayerController';
import { GameMoveableObstacleController } from './obstacles/moveable/GameMoveableObstacleController';
import { GameMoveableObstacleProjectionController } from './obstacles/moveable/GameMoveableObstacleProjectionController';
import { hitTest, remapValue } from '../../Common/Utils';
import { GameGasStationController } from './GameGasStationController';
import { GameObstacleProjectionController } from './obstacles/static/GameObstacleProjectionController';
import { GameObstacleController } from './obstacles/static/GameObstacleController';
import { UICollectableController } from '../UI/UICollectableController';
import { GameCollectableController } from './collectables/GameCollectableController';
import { GameCollectableProjectionController } from './collectables/GameCollectableProjectionController';
import { Sounds } from '../../Constants/Sounds';
import { SoundModel } from '../../Models/SoundModel';
import { isEquals } from '../../Utils/Math';
import { initLevel1, initLevel2, updateLevel1Width, updateLevel2Width } from '../Level';
import { getObstacleConfig } from '../../Common/GameConfig';
import { GeneratorEvenets as GeneratorEvents, LevelEvents, UIEvents } from '../../Constants/Events';
import { SinkaTweener } from '../../Utils/Tweener';
import { SpineContainer } from '../Common/SpineContainer';
import { SpineConst } from '../../Constants/SpineConst';

export interface IGameScreenControllerOptions {
}

enum RoadGridTypeEnum {
    left,
    right
}

const removeObjectMult = 1.5;
export const roadLength = 3200;
const physicsTimer = 0.2;


export class GameScreenController extends AnimationBaseController {
    $model: GameModel;

    private physicsCounter = 0;
    public _gameWidth: number = 0;
    public levelBacks: IHighway;
    public _isLoaded: boolean = false;
    public _isFocused: boolean = true;

    public _container: Container;
    public snowCity: SpineContainer = null;
    public _background: Container;
    public parallaxedBack = {};
    public unparallaxedBack = {};

    public _backgroundSky: PIXI.extras.TilingSprite;
    public _backgroundMountains: PIXI.extras.TilingSprite;
    public _backgroundSun: Sprite;

    public backgroundClouds: Sprite[] = [];
    public backgroundCity: Sprite[] = [];

    public accelVfxTexture: Texture;
    public rayVfxTexture: Texture;
    public glowVfxTexture: Texture;
    public accelerationVfx: PIXI.projection.Sprite3d[] = [];

    private spine;

    public _backgroundForest: PIXI.extras.TilingSprite;
    public _backgroundHorizon: Sprite;

    public _backgroundRoad: Sprite;
    public _roadGradient: Sprite;

    public _camera: PIXI.projection.Camera3d;

    public _player: GamePlayerController;

    public _cameraOffset: number = 0;
    public _cameraTargetOffset: number = 0;
    public _cameraOffsetChangeSpeed: number = 0.1;

    public _projectionContainer: PIXI.projection.Container3d;

    public _roadContainer: PIXI.projection.Container3d;
    public _roadSprites: PIXI.projection.Sprite3d[] = [];
    public _roadSpritesGridLeft: PIXI.projection.Sprite3d[] = [];
    public _roadSpritesGridRight: PIXI.projection.Sprite3d[] = [];
    public _snowRoadTexture: PIXI.Texture;
    public _snowRoadSpritesRight: PIXI.projection.Sprite3d[] = [];
    public _snowRoadSpritesLeft: PIXI.projection.Sprite3d[] = [];
    private roadObjectsContainer: Container;
    private _obstacleProjectionContainer: PIXI.projection.Container3d;

    public deletedObstacles: GameObstacleController[] = [];

    private _collectables: GameCollectableController[] = [];
    private _collectableCounter: number = 0;

    private _collectableProjections: GameCollectableProjectionController[] = [];
    private _collectableProjectionCounter: number = 0;

    private _gasStationProjectionContainer: PIXI.projection.Container3d;
    private _gasStationProjectionGraphics: Graphics;

    private _gasStationContainer: Container;
    private _gasStations: GameGasStationController[] = [];
    private _gasStationCounter: number = 0;

    public _roadTexture: Texture;
    public _roadLineTexture: Texture;

    private _cameraAngle: number = 7.069;//6.96; 
    private _speedMult: number = 1 / 1;

    private _swipeStartX: number;
    private _swipeDist: number = 60;

    public constructor(
        @Inject private _options: IGameScreenControllerOptions,
        @Inject private _gameLogic: GameLogic,
        @Inject private _modelEvents: ModelEvents,
        @Inject private soundLevels: SoundModel,
    ) {
        super('GameScreenController');
        this.levelBacks = Level1;

    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        const sfx = [
            Sounds.COL_CAR, Sounds.COL_DEGEN, Sounds.COL_DPS, Sounds.COL_UNICORN,
            Sounds.TAKE_ITEM, Sounds.USE_BANDITS, Sounds.USE_DEGEN, Sounds.USE_DPS, Sounds.USE_UNICORN,
            Sounds.CHECKPOINT_1, Sounds.CHECKPOINT_2,
        ];

        const sounds = [
            { id: this.getCurrentMusicTrack(), type: SoundType.Music },
        ];

        sfx.forEach((sound) => {
            sounds.push({ id: sound, type: SoundType.SFX });
        });

        const events = {};

        events[GeneratorEvents.addObstacle] = (type: GameObstacleTypeEnum, roadIndex: number) => {
            switch (type) {
                case GameObstacleTypeEnum.flora:
                case GameObstacleTypeEnum.hotel:
                    new Promise(() => {
                        this.createObstacleController({
                            type: type,
                            roadIndex: roadIndex,
                        });
                    });

                    break;
                default:
                    this.createObstacleController({
                        type: type,
                        roadIndex: roadIndex,
                    });
            }


        };
        events[GeneratorEvents.addMoveableObstacle] = (type: GameMoveableObstacleTypeEnum, roadIndex: number) => {
            this.createMoveableObstacleController({
                type: type,
                roadIndex: roadIndex,
            });
        };

        events['removeObstacleByType'] = (type: GameMoveableObstacleTypeEnum | GameObstacleTypeEnum) => {
            const obstacle = this.roadObjectsContainer.children.find(o => {
                if (o['controller']) return o['controller'].getType() === type;
            })['controller'] as GameObstacleController;
            const projection = obstacle.getProjectionController().display as PIXI.projection.Container3d;

            if (obstacle) {
                // badcode :(
                if (type === GameObstacleTypeEnum.horse) {
                    this._gameLogic.useHorseSpeedUp();
                    this.createAccelerationVfx();
                    //one more bad code :)
                    this._gameLogic.coinTransaction(getObstacleConfig(GameObstacleTypeEnum.horse, 'price'));
                    // this.createBoomVfx(projection.x, projection.y);
                    obstacle.boom(() =>
                        this.removeObstacleController(obstacle));
                } else {
                    this.removeObstacleController(obstacle);
                }

            }
        };

        events['removeMoveableObstacleByType'] = (type: GameMoveableObstacleTypeEnum | GameObstacleTypeEnum) => {
            const filtered = this.roadObjectsContainer.children.filter(o=>{
                if (o['controller']) return o['controller'].getType() === type;
            })
            if(!filtered.length) return;
            const display = filtered.pop();
            // const display = this.roadObjectsContainer.children.find(o => {
            //     if (o['controller']) return o['controller'].getType() === type;
            // });
            // if (!display) return;
            const obstacle = display['controller'] as GameMoveableObstacleController;

            if (obstacle) {
                const projection = obstacle.getProjectionController().display as PIXI.projection.Container3d;
                switch (obstacle.getType()) {
                    case GameMoveableObstacleTypeEnum.degen:
                    case GameMoveableObstacleTypeEnum.police:
                    case GameMoveableObstacleTypeEnum.policeCrown:
                    case GameMoveableObstacleTypeEnum.gelandewagen:
                        this._gameLogic.useHorseSpeedUp(3);
                        this._gameLogic.coinTransaction(getObstacleConfig(obstacle.getType(), 'price'));
                        this.createAccelerationVfx();
                        this.createBoomVfx(projection.x, projection.y);
                }

                obstacle.boom(() => {
                    this.removeMoveableObstacleControllerByIndex(obstacle);
                });
            }
        };

        return <IAnimationControllerConfiguration>{
            animationContent: AnimationContents.GAME_SCREEN_JSON,
            libraryName: 'game_screen',
            sounds: sounds,
            resources: {
                levelUIArt: new ResourceItem('LEVEL_UI_JSON', AnimationContents.LEVEL_UI_JSON, ResourceItemType.pixiAnimation),
            },
            keyboard: {
                /*
                 [Key.UP]: {
                     pressed: () => {
                         this._cameraAngle += 0.01;
                     },
                 },
                 [Key.DOWN]: {
                     pressed: () => {
                         this._cameraAngle -= 0.01;
                     },
                 },
                 */
                // [Key.Q]: {
                //     pressed: () => {
                //         this._speedMult = 0;
                //     },
                // },
                // [Key.D]: {
                //     pressed: () => {
                //         this._speedMult = 1;
                //     },
                // },
            },
            childControllers: {
                player: {
                    controller: GamePlayerController,
                    model: this.$model,
                    options: {
                        type: this.$model.currentPlayer.type,
                    },
                },
                boosters: {
                    controller: UICollectableController,
                    instanceName: 'boosters',
                    model: this.$model,
                },
            },
            modelEvents: Object.assign(events, {
                addGasStation: () => {
                    this.createGasStationController();
                },
                addCollectable: (type: GameCollectableTypeEnum, roadIndex: number) => {
                    this.createCollectableController({
                        type: type,
                        projectionController: this.createCollectableProjectionController({
                            type: type,
                            roadIndex: roadIndex,
                        }),
                    });
                },
                removeCollectableByType: (type: GameCollectableTypeEnum) => {
                    const collectable = this._collectables.find(o => o.getType() === type);
                    this.$model.generator.collectables = this.$model.generator.collectables.filter(c => !(c.type == type));
                    if (collectable) {
                        this.removeCollectableControllerByIndex(collectable.getIndex(), collectable.getProjectionController().getIndex());
                    }
                },
            }),
            modelWatch: {
            },
            onPointerDown: e => {
                if (e.globalPosition.y > this.renderer.renderer.height * 0.8) {
                    this._swipeStartX = undefined;
                    return;
                }

                if (this.isChangeRoadLine()) {
                    return;
                }

                if (Environment.type !== EnvironmentTypeEnum.pc) {
                    this._swipeStartX = e.globalPosition.x;
                } else {
                    this.changeDirection(e.globalPosition.x > this.renderer.renderer.width / 2 ? 1 : -1);
                }
            },
            onPointerUp: e => {
                if (Environment.type !== EnvironmentTypeEnum.pc) {
                    const dist = e.globalPosition.x - this._swipeStartX;
                    const direction = Math.sign(dist);
                    const index = this._gameLogic.getPlayerRoadIndex();

                    if (typeof this._swipeStartX !== 'undefined' && Math.abs(dist) > this._swipeDist) {
                        if ((direction >= 0 && index < 1) || (direction < 0 && index >= -1)) {
                            this.changeDirection(direction);
                        }
                    }

                    this._swipeStartX = undefined;
                }
            },
        };
    }

    protected onInitialize(): void {
        PIXI.settings.MIPMAP_TEXTURES = true;
        PIXI.settings.PRECISION_VERTEX = 'mediump';
        this.$model.resetLevel();
        this.$model.currentGas = this.$model.currentPlayer.gasRate;
        this.$model.gameScreen = this;

        if (this.soundManager.isSoundPlaying(Sounds.MUSIC_MENU)) {
            this.soundManager.stop(Sounds.MUSIC_MENU);
        }
        if (!this.soundManager.isSoundPlaying(this.getCurrentMusicTrack())) {
            this.soundManager.play(this.getCurrentMusicTrack(), true, SoundModel.music);
        }

        this._modelEvents.dispatch(this.$model, LevelEvents.showUI);
        this._player = this.$childControllers.get<GamePlayerController>('player');


        let roadIMG = null;
        let roadLineIMG = null;

        switch (this.$model.currentLevel) {
            case 2:
                roadIMG = LVL2_BACKGROUND_ROAD_CENTER_IMG;
                roadLineIMG = LVL2_BACKGROUND_LINE_IMG;
                break;
            case 1:
                roadIMG = BACKGROUND_ROAD_CENTER_IMG;
                roadLineIMG = BACKGROUND_LINE_IMG;
                break;
        }
        this.accelVfxTexture = Texture.fromImage(VFX_ACCELERATION);
        this.rayVfxTexture = Texture.fromImage(VFX_RAY);
        this.glowVfxTexture = Texture.fromImage(VFX_GLOW);
        this._roadTexture = PIXI.Texture.fromImage(roadIMG);
        this._snowRoadTexture = PIXI.Texture.fromImage(SNOW_ROAD);
        this._snowRoadTexture.baseTexture.mipmap = true;
        if (this._roadTexture.baseTexture.hasLoaded) {
            this._roadLineTexture = PIXI.Texture.fromImage(roadLineIMG);
            this.initScene();
        } else {
            this._roadTexture.baseTexture.addListener('loaded', () => {
                this._roadLineTexture = PIXI.Texture.fromImage(roadLineIMG);

                this._roadLineTexture.baseTexture.addListener('loaded', () => {
                    this.initScene();
                });
            });
        }



        const display = this.display as Container;
        display.children.sort((a, b) => {
            if (a.name == 'boosters')
                return 1;
            return 0;
        });

        window['RAY'] = this.createBoomVfx.bind(this);
        window['ACCEL'] = this.createAccelerationVfx.bind(this);

        this.spine = new SpineContainer(this.$model, ['low_fuel'], SpineConst.LowFuel);

        this.display.addChild(this.spine.container);
        this.spine.resetAnimation();
        this.spine.container.scale.set(3);
    }

    protected onResize(width: number, height: number, scale: number): void {
        this._gameWidth = this.width / scale;

        this.scaleX = this.scaleY = Math.min(
            width / scale / this.$model.gameWidth,
            height / scale / this.$model.gameHeight,
        ) / 2;

        const topLeftPoint = this.display.toLocal(new Point(0, 0), this.renderer.stage);
        const topRightPoint = this.display.toLocal(new Point(width, 0), this.renderer.stage);
        const topMidPoint = this.display.toLocal(new Point(width / 2, 0), this.renderer.stage);
        const bottomMidPoint = this.display.toLocal(new Point(width / 2, height), this.renderer.stage);

        const margin = 20;
        this.setPosByQuery('time', topMidPoint.x, topMidPoint.y);
        this.setPosByQuery('boosters', bottomMidPoint.x, bottomMidPoint.y - 100);

        this._player.y = bottomMidPoint.y - 1000;
    }

    protected onUpdate(deltaTime: number): void {
        if (!this.$model) return;
        if (this._isLoaded && this._isFocused && !this.$model.pause) {

            this.updateBackground(deltaTime);
            this._gameLogic.update(deltaTime);
            this.updateObjects(deltaTime);
            if (this.physicsCounter >= physicsTimer) {
                this.updatePhysics();
            } else {
                this.physicsCounter += deltaTime;
            }
        }
    }

    protected onFocus(isFocus: boolean) {
        this._isFocused = isFocus;
        if (!isFocus) {
            if (!this.$model.pause)
                this._modelEvents.dispatch(this.$model, UIEvents.showSettings, true);
            // I know what i'm doing
            this.$model.pause = true;

        }
    }

    private updateTileableObjects(objectsArray: PIXI.projection.Sprite3d[], speed: number): void {
        for (let i = 0; i < objectsArray.length; i++) {
            const sprite = objectsArray[i];

            if (sprite.y >= sprite.height * (objectsArray.length - 1)) {
                sprite.y = objectsArray[0].y - sprite.height;

                objectsArray.splice(objectsArray.indexOf(sprite), 1);
                objectsArray.unshift(sprite);

                break;

            } else {
                sprite.y += speed;
            }
        }
    }

    private updateSnowRoad(objectsArray: PIXI.projection.Sprite3d[], speed: number): void {
        for (let i = 0; i < objectsArray.length; i++) {
            const sprite = objectsArray[i];

            if (sprite.y >= sprite.height * (objectsArray.length - 1)) {
                sprite.y = objectsArray[0].y - sprite.height * 0.98;

                objectsArray.splice(objectsArray.indexOf(sprite), 1);
                objectsArray.unshift(sprite);

                break;

            } else {
                sprite.y += speed;
            }
        }
    }

    protected onDestroy(): void {
        this.roadObjectsContainer.removeChildren();
        this.display.removeChildren();
        super.onDestroy()
    }

    private changeDirection(direction: number): void {
        const currentLine = this._gameLogic.getPlayerRoadIndex();

        let nextLine = currentLine;

        if (direction < 0 && currentLine >= 0) {
            nextLine--;
        }
        if (direction > 0 && currentLine <= 0) {
            nextLine++;
        }

        this._gameLogic.setPlayerRoadIndex(nextLine);
    };

    private updateMoveableObstacleObjects(obstacle: GameMoveableObstacleController, shift: number, deltaTime: number): void {
        const roadLineWidth = this._roadTexture.width / 3;
        const car = this.$model.currentPlayer;

        const obstacleProjection = obstacle.getProjectionController();

        const speedObstacle = obstacle.getType() === GameMoveableObstacleTypeEnum.degen ? shift : (shift - 2 * car.speed * deltaTime * this._speedMult);

        const a = -500 / GameModel.SCALE;
        const b = -500 / GameModel.SCALE;
        switch (obstacle.getType()) {
            case GameMoveableObstacleTypeEnum.degen:
                break;
            default:
                obstacle.setProjection(a, b);
        }


        const obstacleOffset = obstacleProjection.getRoadLineOffset();
        const obstacleOffsetTarget = obstacleProjection.getRoadLineTargetOffset();
        const obstacleOffsetChangeSpeed = obstacleProjection.getRadLineOffsetChangeSpeed();

        const newTargetOffset = obstacleProjection.getRoadIndex() * roadLineWidth;

        const newOffset = obstacleOffset === undefined ? newTargetOffset : obstacleOffset + (obstacleOffsetTarget - obstacleOffset) * obstacleOffsetChangeSpeed;

        obstacleProjection.display.x = newOffset + obstacleProjection.getRoadMarginPoint().x;
        obstacleProjection.display.y += speedObstacle;
        obstacle.visible = true;

        obstacleProjection.setRoadLineWidth(roadLineWidth);
        obstacleProjection.setRoadLineTargetOffset(newTargetOffset);
        obstacleProjection.setRoadLineOffset(newOffset);

        if (!obstacle.destroyed)
            switch (obstacle.getType()) {
                case GameMoveableObstacleTypeEnum.degen:
                    if ((obstacle.visible)) {
                        const moveableDistance = 580;
                        const direction = -obstacleProjection.getRoadIndex();

                        if ((direction > 0 && obstacleProjection.x >= moveableDistance) || (direction < 0 && obstacleProjection.x <= -moveableDistance)) {
                            //this.removeMoveableObstacleControllerByIndex(obstacle.getIndex(), obstacleProjection.getIndex());
                            //break;

                        } else {
                            const marginPoint = obstacleProjection.getRoadMarginPoint();
                            const marginSpeed = 1 * car.speedMax * deltaTime;// FIXME: hardcode degen speed

                            obstacle.setDirection(direction);

                            obstacle.scaleInvertor = (direction < 0) ? true : false;
                            obstacleProjection.setRoadMarginPoint(new Point(marginPoint.x + marginSpeed * direction, marginPoint.y));
                        }
                    }
                    break;
                case GameMoveableObstacleTypeEnum.police:
                case GameMoveableObstacleTypeEnum.policeCrown:
                    if ((obstacleProjection.y > 580) && (!obstacleProjection.policeSwapedLine)) {

                        obstacleProjection.swapRoadIndex();
                        obstacleProjection.policeSwapedLine = true;
                    }
                    break;
                case GameMoveableObstacleTypeEnum.gelandewagen:
                    if ((obstacleProjection.y / obstacleProjection.gelikSwapedLines) > 1000) {
                        obstacleProjection.swapRoadIndex();
                        obstacleProjection.gelikSwapedLines++;
                    }
                    break;
            }


        if (obstacleProjection.display.y >= roadLength) {
            this.removeMoveableObstacleControllerByIndex(obstacle);
        }


    }


    private updateCollectableObjects(collectable: GameCollectableController, speed: number): void {
        const roadLineWidth = this._roadTexture.width / 3;// * GameModel.SCALE;
        const speedCollectable = speed;

        // for (let i = 0; i < this._collectableProjections.length; i++) {
        const collectableProjection = collectable.getProjectionController();

        collectableProjection.display.x = collectableProjection.getRoadIndex() * roadLineWidth;
        collectableProjection.display.y += speedCollectable;
        collectable.visible = true;

        if (collectableProjection.display.y >= this._roadSprites[0].height * 1.5 / GameModel.SCALE) {
            this.$model.generator.collectables = this.$model.generator.collectables.filter(c => !(c.type == collectable.getType()));
            this.removeCollectableControllerByIndex(collectable.getIndex(), collectableProjection.getIndex());

        }
        // }


    }

    private updateGasStationObjects(gasStation: GameGasStationController, speed: number): void {
        const speedGasStation = speed * 0.8;

        // for (let i = 0; i < this._gasStations.length; i++) {
        // const globalProjectionPoint = this._gasStationProjectionContainer.parent.toGlobal(this._gasStationProjectionContainer.position);
        // const localPoint = gasStation.display.parent.toLocal(globalProjectionPoint, this.renderer.stage);

        // gasStation.display.x = localPoint.x;
        // gasStation.display.y = localPoint.y;

        gasStation.options.projection.y += speed;

        if ((gasStation.options.projection.y >= 2900) &&
            (!gasStation.applied)) {
            if (Math.random() > 0.5)
                this.soundManager.play(Sounds.CHECKPOINT_2, false, this.soundLevels.collisionVolume);
            else
                this.soundManager.play(Sounds.CHECKPOINT_1, false, this.soundLevels.collisionVolume);

            this._gameLogic.applyGasStation();

            console.log(this.snowCity);
            this.snowCity.resetAnimation();
            this.snowCity.setAnimation(`location_${this.$model.currentLevel}_back_salut`, false);

            gasStation.applied = true;
        }

        if (gasStation.display.y >= this._roadSprites[0].height * 1.5 / GameModel.SCALE) {


            this.removeGasStationControllerByIndex(gasStation);
            // this._gasStationProjectionContainer.y = 0;
        } else {
            // this._gasStationProjectionContainer.y += speedGasStation;
        }
        // }

    }

    private updateObjects(deltaTime: number): void {
        if (!this.$model) return;
        if (!this._roadSprites.length) {
            return;
        }
        try { this._player.updateBounds(); }
        catch {
            console.warn("cant update bounds")
        }

        const speed = this.$model.currentPlayer.speed * 5 * deltaTime * this._speedMult;
        const roadLineWidth = this._roadTexture.width / 3 * GameModel.SCALE;

        this._camera.euler.x = this._cameraAngle;

        this._cameraTargetOffset = this._gameLogic.getPlayerRoadIndex() * roadLineWidth;
        this._cameraOffset = this._cameraOffset === undefined ? this._cameraTargetOffset : this._cameraOffset + (this._cameraTargetOffset - this._cameraOffset) * this._cameraOffsetChangeSpeed;

        if (!this.isChangeRoadLine()) {
            this._player.setDirection(0);
        }
        this._projectionContainer.x = -this._cameraOffset;

        this.updateTileableObjects(this._roadSprites, speed);
        this.updateTileableObjects(this._roadSpritesGridLeft, speed);
        this.updateTileableObjects(this._roadSpritesGridRight, speed);
        this.updateSnowRoad(this._snowRoadSpritesRight, speed);
        this.updateSnowRoad(this._snowRoadSpritesLeft, speed);

        this.updateRoadObjects(speed, deltaTime);
        this.updateAccelerationVfx(deltaTime);
    }

    updateRoadObjects(speed: number, deltaTime: number) {

        const roadLineWidth = this._roadTexture.width / 3;
        const car = this.$model.currentPlayer;
        this.roadObjectsContainer.children.forEach((display) => {
            if (display['controller'])
                switch (display['controller'].getObjectType()) {
                    case RoadObjectType.obstacle:
                        const obstacle = display['controller'] as GameObstacleController;
                        const obstacleProjection = obstacle.getProjectionController();
                        if (obstacle) {
                            obstacleProjection.display.x = obstacleProjection.getRoadIndex() * roadLineWidth;
                            obstacleProjection.display.y += speed;
                            obstacle.visible = true;
                            if (obstacleProjection.display.y >= roadLength) {
                                this.removeObstacleController(obstacle);
                                break;
                            }
                        }

                        //update obstacle
                        const globalProjectionPoint = obstacle._projectionController.display.parent.toGlobal(obstacle._projectionController.display.position);
                        const localPoint = obstacle.display.parent.toLocal(globalProjectionPoint, this.renderer.stage);

                        let scale = remapValue(globalProjectionPoint.y,
                            this.renderer.renderer.height * 0.25, this.renderer.renderer.height,
                            obstacle._projectionScale[0] * GameModel.SCALE, obstacle._projectionScale[1] * GameModel.SCALE,
                        );

                        if ((obstacle._type === GameObstacleTypeEnum.horse)) {
                            scale = scale * (obstacle._projectionController.getRoadIndex() < 0 ? 1 : -1);
                        }

                        obstacle.alpha = Math.min(1, obstacle.getProjectionController().display.y / 500);

                        obstacle.display.x = localPoint.x;
                        obstacle.display.y = localPoint.y;
                        obstacle.display.scale.set(scale, Math.abs(scale));
                        break;


                    case RoadObjectType.movableObstacle:
                        const movableObstacle = display['controller'] as GameMoveableObstacleController;
                        this.updateMoveableObstacleObjects(movableObstacle, speed, deltaTime);
                        break;
                    case RoadObjectType.collectable:
                        const collectable = display['controller'] as GameCollectableController;
                        this.updateCollectableObjects(collectable, speed);
                        break;
                    case RoadObjectType.gasStation:
                        const gasStation = display['controller'] as GameGasStationController;
                        this.updateGasStationObjects(gasStation, speed);
                        break;

                }
            else {
                this.roadObjectsContainer.removeChild(display);
            }
        })
    }

    private isChangeRoadLine(): boolean {
        return !isEquals(this._cameraTargetOffset, this._cameraOffset, 60);
    }



    private backCounter = 0;

    private updateBackground(frameRate: number): void {

        const parallaxSprites: Sprite[] = Object.values(this.parallaxedBack);
        for (let i = 0; i < parallaxSprites.length; i++) {
            const sprite = parallaxSprites[i];
            sprite.x = -this._cameraOffset / 200 * (i * 10);
        }
    }

    private initScene(): void {
        this._container = new Container();
        this.display.addChildAt(this._container, 0);

        this._background = new Container();
        this._background.scale.set(2.75);
        this._background.position.set(0, -800);

        switch (this.$model.currentLevel) {
            case 1:
                initLevel1(this);
                break;
            case 2:
                initLevel2(this);
                break;
        }

        // Camera
        this._camera = new PIXI.projection.Camera3d();
        this._camera.setPlanes(300, -10000, 10000, false);
        this._camera.euler.x = this._cameraAngle;
        this._camera.position.set(0, this._background.y);
        this._camera.position3d.z = -20;
        this._container.addChild(this._camera);

        // Background
        this._container.addChild(this._background);

        // Road
        this._roadGradient = new PIXI.projection.Sprite2d(PIXI.Texture.from(Level1.roadGradient));
        this._roadGradient.anchor.set(0.5, 0);
        this._roadGradient.scale.set(10, 0.25);
        this._roadGradient.position.copy(this._background.position);
        this._container.addChild(this._roadGradient);

        this._backgroundHorizon = new Sprite(PIXI.Texture.from(Level1.horizon));
        this._backgroundHorizon.anchor.set(0.5, 0.5);
        this._backgroundHorizon.scale.set(10, 1);
        this._backgroundHorizon.position.set(0, this._background.y + 25);
        this._container.addChild(this._backgroundHorizon);

        this.roadObjectsContainer = new Container();
        this._container.addChild(this.roadObjectsContainer);

        this._gasStationContainer = new Container();
        this._container.addChild(this._gasStationContainer);

        // Projection
        this._projectionContainer = new PIXI.projection.Container3d();
        this._projectionContainer.scale.set(1 * GameModel.SCALE, 0.2 * GameModel.SCALE);
        this._camera.addChild(this._projectionContainer);

        this._roadContainer = new PIXI.projection.Container3d();
        this._projectionContainer.addChild(this._roadContainer);

        this.createRoadSprites();

        this._obstacleProjectionContainer = new PIXI.projection.Container3d();
        this._projectionContainer.addChild(this._obstacleProjectionContainer);

        this._gasStationProjectionContainer = new PIXI.projection.Container3d();
        this._projectionContainer.addChild(this._gasStationProjectionContainer);

        (this._gasStationProjectionContainer as PIXI.projection.Container3d).proj.affine = 2;

        this.createRoadGridSprites(RoadGridTypeEnum.left);
        this.createRoadGridSprites(RoadGridTypeEnum.right);
        this.createSnowRoad(RoadGridTypeEnum.right);
        this.createSnowRoad(RoadGridTypeEnum.left);

        switch (this.$model.currentLevel) {
            case 1:
                updateLevel1Width(this, this._gameWidth * 4);
                break;
            case 2:
                updateLevel2Width(this, this._gameWidth * 4);
                break;
        }

        setTimeout(() => {
            this._isLoaded = true;
            this._gameLogic.start();
        }, 200)
    }

    public updateAccelerationVfx(deltaTime: number) {
        const speed = 1500;
        this.accelerationVfx.forEach(sprite => {
            sprite.position3d.y -= speed * deltaTime;
            if (sprite.position3d.y <= 0) {
                this._roadContainer.removeChild(sprite);
                this.accelerationVfx = this.accelerationVfx.filter((a) => a != sprite);
            }
        });
    }

    public createAccelerationVfx(affine = 1) {
        const sprite = new projection.Sprite3d(this.accelVfxTexture);
        sprite.anchor.set(0.5, 0.5);
        console.log(sprite.proj.affine);
        sprite.proj.affine = affine;
        sprite.position3d.set(this._gameLogic.getPlayerRoadIndex() * 420, roadLength);
        this._roadContainer.addChild(sprite);
        this.accelerationVfx.push(sprite);
    }

    public createBoomVfx(x: number, y: number) {
        const ray = new projection.Sprite3d(this.rayVfxTexture);
        const glow = new Sprite(this.glowVfxTexture);
        glow.anchor.set(0.5, 0.5);
        glow.y = -180;
        this._player.display.addChildAt(glow, 0);

        ray.anchor.set(0.5, 0);
        ray.position3d.y = y;
        ray.position3d.x = x;
        const height = Math.sqrt(Math.pow(x - this._cameraOffset * 1.2, 2) + Math.pow(2900 - y, 2));

        ray.scale3d.y = height / 750;
        console.log(ray.height);
        ray.euler.z = Math.asin((x - this._cameraOffset * 1.5) / height);
        this._obstacleProjectionContainer.addChild(ray);

        const tweener = new SinkaTweener(ray);
        tweener.animateTo({ alpha: 0.5 }, 0.05, () => {
            this._obstacleProjectionContainer.removeChild(ray);
            this._player.display.removeChild(glow);
        });
    }

    private createRoadSprites(count: number = 4): void {
        for (let i = 0; i < count; i++) {
            const sprite = new PIXI.projection.Sprite3d(this._roadTexture);

            sprite.anchor.set(0.5, 0);
            sprite.position.set(0, sprite.height * (i - 1));

            this._roadContainer.addChild(sprite);
            this._roadSprites.push(sprite);
        }
    }

    private createRoadGridSprites(type: RoadGridTypeEnum, count: number = 4): void {
        for (let i = 0; i < count; i++) {
            const sprite = new PIXI.projection.Sprite3d(this._roadLineTexture);
            const y = sprite.height * (i - 1);

            if (type === RoadGridTypeEnum.left) {
                sprite.anchor.set(1, 0);
                sprite.position.set(-this._roadTexture.width / 2 + 32, y);
                this._roadSpritesGridLeft.push(sprite);

            } else if (type === RoadGridTypeEnum.right) {
                sprite.anchor.set(0, 0);
                sprite.position.set(this._roadTexture.width / 2 - 32, y);
                this._roadSpritesGridRight.push(sprite);
            }

            sprite.scale.set(2, 1);

            this._roadContainer.addChild(sprite);
        }
    }

    private createSnowRoad(type: RoadGridTypeEnum, count: number = 6): void {
        for (let i = 0; i < count; i++) {
            const sprite = new PIXI.projection.Sprite3d(this._snowRoadTexture);
            sprite.scale.set(2, 2);
            const y = sprite.height * 0.98 * (i - 1);

            if (type === RoadGridTypeEnum.left) {
                sprite.anchor.set(0, 0);
                sprite.scale.x = -sprite.scale.x;
                sprite.position.set(-sprite.width / 2 + 32, y);
                this._snowRoadSpritesLeft.push(sprite);

            } else if (type === RoadGridTypeEnum.right) {
                sprite.anchor.set(0, 0);
                sprite.position.set(sprite.width / 2 - 32, y);
                this._snowRoadSpritesRight.push(sprite);
            }


            this._roadContainer.addChild(sprite);
        }
    }

    private createMoveableObstacleProjectionController(options: { type: GameMoveableObstacleTypeEnum, roadIndex: number }): GameMoveableObstacleProjectionController {
                const controller = this.addChildController({
            model: this.$model,
            controller: GameMoveableObstacleProjectionController,
            options: { ...options, index: this.$model.gameScreen_MoveableObstacleProjectionCounter },
        }, `moveable_obstacle_projection_${this.$model.gameScreen_MoveableObstacleProjectionCounter}`) as GameMoveableObstacleProjectionController;

        controller.display.convertTo3d();
        (controller.display as PIXI.projection.Container3d).proj.affine = 0;
        if (!controller.display) {
            this.removeChildController(controller);
            return this.createMoveableObstacleProjectionController(options);
        }
        else
            this._obstacleProjectionContainer.addChildAt(controller.display, 0);
        this.$model.gameScreen_MoveableObstacleProjectionCounter++;

        return controller;
    }

    public createMoveableObstacleController(options: { type: GameMoveableObstacleTypeEnum, roadIndex: number }): GameMoveableObstacleController {
        const projection = this.createMoveableObstacleProjectionController(options);
        options['projectionController'] = projection;
        const controller = this.addChildController({
            model: this.$model,
            controller: GameMoveableObstacleController,
            options: { ...options, index: this.$model.gameScreen_MoveableObstacleCounter },
        }, `moveable_obstacle_${this.$model.gameScreen_MoveableObstacleCounter}`) as GameMoveableObstacleController;
        this.$model.gameScreen_MoveableObstacleCounter++;

        try {
            this.roadObjectsContainer.addChildAt(controller.display, 0);
        }
        catch (e) {
            console.warn("cant add moveable obstacle");
            console.warn(e);
            this.removeChildController(controller);
            this.removeChildController(projection);
        }


        return controller;
    }

    private removeMoveableObstacleControllerByIndex(controller: GameMoveableObstacleController): void {

        //const controller = //this.$childControllers.get<GameMoveableObstacleController>(`moveable_obstacle_${index}`);
        const projectionController = controller.getProjectionController();//this.$childControllers.get<GameMoveableObstacleProjectionController>(`moveable_obstacle_projection_${projectionIndex}`);
        if (this.$childControllers) {
            this.removeChildController(controller);
            this.removeChildController(projectionController);
        }

    }

    private createObstacleProjectionController(options: { type: GameObstacleTypeEnum, roadIndex: number }): GameObstacleProjectionController {
        const controller = this.addChildController({
            model: this.$model,
            controller: GameObstacleProjectionController,
            options: { ...options, index: this.$model.gameScreen_ObstacleProjectionCounter },
        }, `obstacle_projection_${this.$model.gameScreen_ObstacleProjectionCounter}`) as GameObstacleProjectionController;

        controller.display.convertTo3d();
        (controller.display as PIXI.projection.Container3d).proj.affine = 2;

        this._obstacleProjectionContainer.addChildAt(controller.display, 0);
        this.$model.gameScreen_ObstacleProjectionCounter++;

        return controller;
    }

    public createObstacleController(options: { type: GameObstacleTypeEnum, roadIndex: number }, shift = 0): GameObstacleController {
        let projection: GameObstacleProjectionController = this.createObstacleProjectionController(options);
        projection.y = -shift;
        options['projectionController'] = projection;

        if ((!this.deletedObstacles.length) || (options.type == GameObstacleTypeEnum.horse)) {

            const controller = this.addChildController({
                model: this.$model,
                controller: GameObstacleController,
                options: { ...options, index: this.$model.gameScreen_MoveableObstacleCounter },
            }, `obstacle_${this.$model.gameScreen_MoveableObstacleCounter}`) as GameObstacleController;

            this.roadObjectsContainer.addChildAt(controller.display, 0);
            this.$model.gameScreen_MoveableObstacleCounter++;

            return controller;
        } else {

            const controller = this.deletedObstacles.pop();
            controller.destroyed = false;
            controller.options.type = options.type;
            controller.options.projectionController = projection;
            controller._projectionController = projection;
            controller._type = options.type;
            controller.reskinObstacle();
            this.roadObjectsContainer.addChildAt(controller.display, 0);
            return controller;
        }
    }

    private removeObstacleController(controller: GameObstacleController): void {
        if (controller) {
            // const controller = this.$childControllers.get<GameObstacleController>(`obstacle_${index}`);
            const projectionController = controller.getProjectionController();


            // this._obstacles.splice(this._obstacles.indexOf(controller), 1);


            if (controller.display) {
                if (controller.getType() != GameObstacleTypeEnum.horse) {
                    controller.destroyed = true;
                    controller._type = GameObstacleTypeEnum.none;
                    controller.options.type = GameObstacleTypeEnum.none;
                    controller._projectionController = null;
                    controller.visible = false;
                    this.deletedObstacles.push(controller);
                    this.roadObjectsContainer.removeChild(controller.display);
                } else {
                    this.removeChildController(controller);
                }
            } else {
                this.removeChildController(controller);
            }

            this.removeChildController(projectionController);
            projectionController.display.destroy();


        }
    }

    private createCollectableProjectionController(options: { type: GameCollectableTypeEnum, roadIndex: number }): GameCollectableProjectionController {
        const controller = this.addChildController({
            model: this.$model,
            controller: GameCollectableProjectionController,
            options: { ...options, index: this._collectableProjectionCounter },
        }, `collectable_projection_${this._collectableProjectionCounter}`) as GameCollectableProjectionController;

        controller.display.convertTo3d();
        (controller.display as PIXI.projection.Container3d).proj.affine = 2;

        this._obstacleProjectionContainer.addChildAt(controller.display, 0);
        this._collectableProjections.push(controller);
        this._collectableProjectionCounter++;

        return controller;
    }

    private createCollectableController(options: { type: GameCollectableTypeEnum, projectionController: GameCollectableProjectionController }): GameCollectableController {
        const controller = this.addChildController({
            model: this.$model,
            controller: GameCollectableController,
            options: { ...options, index: this._collectableCounter },
        }, `collectable_${this._collectableCounter}`) as GameCollectableController;

        this.roadObjectsContainer.addChildAt(controller.display, 0);
        this._collectables.push(controller);
        this._collectableCounter++;

        return controller;
    }

    private removeCollectableControllerByIndex(index: number, projectionIndex: number): void {
        if (index > -1) {
            const controller = this.$childControllers.get<GameCollectableController>(`collectable_${index}`);
            const projectionController = this.$childControllers.get<GameCollectableProjectionController>(`collectable_projection_${projectionIndex}`);

            if (this._collectables.includes(controller)) {
                this._collectables.splice(this._collectables.indexOf(controller), 1);
                this.removeChildController(controller);
            }

            if (this._collectableProjections.includes(projectionController)) {
                this._collectableProjections.splice(this._collectableProjections.indexOf(projectionController), 1);
                this.removeChildController(projectionController);
            }
        }
    }

    private createGasStationController(): void {
        this._gasStationProjectionContainer.y = 0;
        const projection = this.createObstacleProjectionController({ type: GameObstacleTypeEnum.none, roadIndex: 0 });

        const controller = this.addChildController({
            model: this.$model,
            controller: GameGasStationController,
            options: {
                index: this._gasStationCounter,
                projection,
            },
        }, `gas_station_${this._gasStationCounter}`) as GameGasStationController;

        this.roadObjectsContainer.addChildAt(controller.display, 0);
        // this._gasStations.push(controller);
        this._gasStationCounter++;

        // this.updateObjects(0);
    }

    private removeGasStationControllerByIndex(controller: GameGasStationController): void {
        this.removeChildController(controller.options.projection);
        this.removeChildController(controller);

    }

    private getCurrentMusicTrack(): string {
        switch (this.$model.currentLevel) {
            case 1:
                return Sounds.MUSIC_TRACK_1;

            case 2:
                return Sounds.MUSIC_TRACK_2;
        }
        return undefined;
    }

    updatePhysics() {
        if (!this.$model) return;
        Object.keys(this.$model.collectablesObstacle).forEach((key) => {
            this.$model.collectablesObstacle[key] = 0;
        });
        const width = this._gameWidth * 4;
        this.roadObjectsContainer.children.forEach(display => {
            switch (display['controller'].getObjectType()) {
                case RoadObjectType.obstacle:
                    const obstacle = display['controller'] as GameObstacleController;
                    this.obstaclePhysics(obstacle);
                    break;
                case RoadObjectType.movableObstacle:
                    const movableobstacle = display['controller'] as GameMoveableObstacleController;
                    this.movablePhysics(movableobstacle);
                    break;
                case RoadObjectType.collectable:
                    const collectable = display['controller'] as GameCollectableController;
                    this.collectablePhysics(collectable);
                    break;

            }

            if (this.backCounter) {
                switch (this.$model.currentLevel) {
                    case 1:
                        updateLevel1Width(this, width);
                        break;
                    case 2:
                        updateLevel2Width(this, width);
                        break;
                }
                this.backCounter = 0;
            } else {
                this.backCounter = 1;
            }


            this.roadObjectsContainer.children.forEach((display) => {
                const value = display['controller'];

                if ((!value.destroyed) && (display.visible))
                    switch (value.getType()) {
                        case GameMoveableObstacleTypeEnum.degen:
                            this.$model.collectablesObstacle[GameCollectableTypeEnum.bat]++;
                            break;
                        case GameMoveableObstacleTypeEnum.police:
                        case GameMoveableObstacleTypeEnum.policeCrown:
                            this.$model.collectablesObstacle[GameCollectableTypeEnum.crypto]++;
                            break;
                        case GameMoveableObstacleTypeEnum.gelandewagen:
                            this.$model.collectablesObstacle[GameCollectableTypeEnum.gun]++;
                            break;
                    }
            });

            return;
        });
    }

    async obstaclePhysics(obstacle: GameObstacleController) {
        //update obstacles
        const obstacleProjection = obstacle.getProjectionController();
        const roadIndex = obstacle.getRoadIndex();

        switch (obstacle.getType()) {
            case GameObstacleTypeEnum.flora:
            case GameObstacleTypeEnum.hotel:
            case GameObstacleTypeEnum.roadSign:
                break;
            case GameObstacleTypeEnum.horse:
                if ((!obstacle.destroyed) && (obstacle.visible)) {
                    this.$model.collectablesObstacle[GameCollectableTypeEnum.horse]++;
                }
            default:

                if (obstacleProjection.y < 1500) break;
                let hitTestWithPlayer = hitTest(obstacle.display.getBounds(), this._player.bounds);

                hitTestWithPlayer = hitTestWithPlayer && (roadIndex === this._gameLogic.getPlayerRoadIndex());
                if ((hitTestWithPlayer) && (!obstacle.destroyed)) {
                    switch (obstacle.getType()) {
                        case GameObstacleTypeEnum.horse:
                            this.soundManager.play(Sounds.COL_UNICORN, false, this.soundLevels.collisionVolume);
                            break;
                        case GameObstacleTypeEnum.none:
                        case GameObstacleTypeEnum.hole:
                            this._modelEvents.dispatch(this.$model, 'removeObstacleByType', GameObstacleTypeEnum.roadSign, true);
                        case GameObstacleTypeEnum.stone:
                            this.soundManager.play(Sounds.COL_CAR, false, this.soundLevels.collisionVolume);
                            break;
                    }
                    this._gameLogic.onPlayerHitWithObstacle();
                    this.removeObstacleController(obstacle);

                    break;
                }

        }
    }

    async collectablePhysics(collectable: GameCollectableController) {
        const collectableProjection = collectable.getProjectionController();
        const roadIndex = collectable.getRoadIndex();
        const hitTestWithPlayer = (roadIndex === this._gameLogic.getPlayerRoadIndex()) && hitTest(collectable.display.getBounds(), this._player.bounds);

        if (hitTestWithPlayer) {
            // collectables hit test
            if (!collectable.collected) {
                this.soundManager.play(Sounds.TAKE_ITEM, false, this.soundLevels.collisionVolume);
                collectable.collect(() => {
                    this.removeCollectableControllerByIndex(collectable.getIndex(), collectableProjection.getIndex());
                });
            }

            this._gameLogic.takeCollectable(collectable.getType());

        }


    }

    async movablePhysics(obstacle: GameMoveableObstacleController) {
        const obstacleProjection = obstacle.getProjectionController();
        const roadIndex = obstacle.getRoadIndex();
        const isSameRoad = roadIndex === this._gameLogic.getPlayerRoadIndex();
        const hitTestWithPlayer = hitTest(this._player.bounds, obstacle.display.getBounds());

        try {
            if ((!obstacle.destroyed) && (obstacle.visible))
                switch (obstacle.getType()) {
                    case GameMoveableObstacleTypeEnum.degen:
                        this.$model.collectablesObstacle[GameCollectableTypeEnum.bat]++;
                        break;
                    case GameMoveableObstacleTypeEnum.police:
                    case GameMoveableObstacleTypeEnum.policeCrown:
                        this.$model.collectablesObstacle[GameCollectableTypeEnum.crypto]++;
                        break;
                    case GameMoveableObstacleTypeEnum.gelandewagen:
                        this.$model.collectablesObstacle[GameCollectableTypeEnum.gun]++;
                        break;
                }
        } catch {

        }

        if ((hitTestWithPlayer) && (!obstacle.destroyed)) {
            if (obstacle.getType() === GameMoveableObstacleTypeEnum.degen) {
                this.soundManager.play(Sounds.COL_DEGEN, false, this.soundLevels.collisionVolume);

                this.removeMoveableObstacleControllerByIndex(obstacle);
                this._gameLogic.onPlayerHitWithObstacle();

            } else {
                if (isSameRoad) {
                    switch (obstacle.getType()) {
                        case GameMoveableObstacleTypeEnum.police:
                            this.soundManager.play(Sounds.COL_DPS, false, this.soundLevels.collisionVolume);
                            break;
                        default:
                            this.soundManager.play(Sounds.COL_CAR, false, this.soundLevels.collisionVolume);
                            break;
                    }

                    this.removeMoveableObstacleControllerByIndex(obstacle);
                    this._gameLogic.onPlayerHitWithObstacle();

                }
            }
        }


    }
}
